import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { ArrowLeft, Heart, Gift, Users, Copy, ExternalLink, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatEUR = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0);
const formatDate = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleDateString('it-IT'); } catch { return s; } };

const ClientGiftLists = () => {
  const { user } = useAuth();
  const [lists, setLists] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchLists(); }, []);

  const fetchLists = async () => {
    try { const r = await axios.get(`${API}/my/gift-lists`); setLists(r.data || []); }
    catch (e) { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  };

  const openDetail = async (l) => {
    try {
      const r = await axios.get(`${API}/my/gift-lists/${l.id}/contributions`);
      setDetail(r.data);
    } catch { toast.error('Errore'); }
  };

  const copyPublicLink = (slug) => {
    navigator.clipboard.writeText(`${window.location.origin}/gift/${slug}`);
    toast.success('Link copiato');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-pink-50" data-testid="client-gift-lists-page">
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3">
          <Link to="/dashboard"><Button variant="outline" size="sm"><ArrowLeft className="w-4 h-4 mr-1" />Dashboard</Button></Link>
          <div className="flex items-center gap-2"><Heart className="w-6 h-6 text-pink-500" /><h1 className="text-lg sm:text-xl font-bold text-slate-800">Le Mie Liste</h1></div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pink-600"></div></div>
        ) : lists.length === 0 ? (
          <Card><CardContent className="text-center py-12 text-slate-500">
            <Gift className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">Nessuna lista attiva</p>
            <p className="text-sm">Chiedi alla tua agenzia di crearne una per te.</p>
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {lists.map((l) => (
              <Card key={l.id} data-testid={`my-list-${l.id}`}>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    {l.type === 'wedding' ? <Heart className="w-5 h-5 text-pink-500" /> : <Gift className="w-5 h-5 text-indigo-500" />}
                    <CardTitle className="text-base">{l.title}</CardTitle>
                  </div>
                  <CardDescription>
                    {l.event_date && `Evento: ${formatDate(l.event_date)}`}
                    {l.event_date && l.goal_amount && ' · '}
                    {l.goal_amount && `Obiettivo: ${formatEUR(l.goal_amount)}`}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-center">
                    <div className="text-xs text-emerald-600 uppercase font-semibold">Raccolto</div>
                    <div className="text-2xl font-bold text-emerald-800">{formatEUR(l.confirmed_total)}</div>
                    <div className="text-xs text-emerald-600 mt-1">{l.confirmed_count} amici hanno partecipato</div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" onClick={() => openDetail(l)} className="bg-pink-600 hover:bg-pink-700" data-testid={`view-${l.id}`}>
                      <Users className="w-4 h-4 mr-1" />Vedi chi ha partecipato
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => copyPublicLink(l.public_slug)}>
                      <Copy className="w-4 h-4 mr-1" />Copia link
                    </Button>
                    <a href={`/gift/${l.public_slug}`} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="outline"><ExternalLink className="w-4 h-4" /></Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {detail.gift_list.type === 'wedding' ? <Heart className="w-5 h-5 text-pink-500" /> : <Gift className="w-5 h-5 text-indigo-500" />}
                  {detail.gift_list.title}
                </DialogTitle>
                <DialogDescription>
                  Totale raccolto: <strong>{formatEUR(detail.total)}</strong> da {detail.count} amici
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                {detail.contributions.length === 0 ? (
                  <p className="text-center text-slate-500 py-6">Nessun contributo confermato al momento</p>
                ) : detail.contributions.map((c, i) => (
                  <div key={i} className="border border-slate-200 rounded p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <strong>{c.guest_name}</strong>
                          <Badge className="bg-emerald-600">{formatEUR(c.confirmed_amount)}</Badge>
                        </div>
                        <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-2">
                          {c.guest_email && <a href={`mailto:${c.guest_email}`} className="text-blue-600 hover:underline flex items-center gap-1"><Mail className="w-3 h-3" />{c.guest_email}</a>}
                          {c.guest_phone && <a href={`tel:${c.guest_phone}`} className="text-blue-600 hover:underline flex items-center gap-1"><Phone className="w-3 h-3" />{c.guest_phone}</a>}
                          <span>{formatDate(c.confirmed_at)}</span>
                        </div>
                        {c.message && <div className="text-sm text-slate-700 italic mt-2">&ldquo;{c.message}&rdquo;</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientGiftLists;
